/**
 * `/v1/ops/firewall-rulesets/*` — the admission firewall ruleset store (#1760).
 *
 * Pure functions taking a trailing injectable `fetchImpl`, same shape as
 * `egressProfiles.ts`. Every write here produces a DRAFT version; nothing on
 * this module changes what a live request sees except `activateFirewallRuleset`,
 * which is why it is the only one whose caller is expected to confirm first.
 */
import type {
  ActivateFirewallRulesetRequest,
  FirewallRulesetIssue,
  CreateFirewallRulesetRequest,
  CreateFirewallRulesetVersionRequest,
  DeleteFirewallRulesetResponse,
  FirewallRulesetMutationResponse,
  FirewallRulesetScope,
  FirewallRulesetSummary,
  FirewallRulesetVersionRecord,
  GetFirewallRulesetResponse,
  GetFirewallRulesetVersionResponse,
  ListFirewallRulesetsResponse,
  ReplayFirewallRulesetRequest,
  ReplayFirewallRulesetResponse,
} from '@nadoutong/contracts';
import { opsApiRequest, OpsApiError } from './client.ts';

const BASE_PATH = '/v1/ops/firewall-rulesets';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The structured issue list from a rejected write, or an empty list when the
 * failure was anything else.
 *
 * The gateway relays `issues` as a SIBLING of `error`, so it arrives on
 * `OpsApiError.body` rather than on `details`. Callers render this list next to
 * the fields that produced it; an empty result means "this failure has no
 * per-rule detail" and the caller falls back to the envelope message.
 */
export function firewallIssuesFromError(error: unknown): readonly FirewallRulesetIssue[] {
  if (!(error instanceof OpsApiError) || error.body === null) return [];
  const issues = error.body.issues;
  if (!Array.isArray(issues)) return [];
  return issues.flatMap((issue): readonly FirewallRulesetIssue[] => {
    if (!isRecord(issue)) return [];
    const { code, message, rulesetId, ruleId, at } = issue;
    if (typeof code !== 'string' || typeof message !== 'string') return [];
    return [{
      code,
      message,
      ...(typeof rulesetId === 'string' ? { rulesetId } : {}),
      ...(typeof ruleId === 'string' ? { ruleId } : {}),
      ...(typeof at === 'string' ? { at } : {}),
    }];
  });
}

export interface ListFirewallRulesetsFilter {
  readonly scope?: FirewallRulesetScope;
  readonly owner_key_bucket_id?: string;
  readonly owner_channel_id?: string;
}

function listPath(filter: ListFirewallRulesetsFilter): string {
  const params = new URLSearchParams();
  if (filter.scope !== undefined) params.set('scope', filter.scope);
  if (filter.owner_key_bucket_id !== undefined) {
    params.set('owner_key_bucket_id', filter.owner_key_bucket_id);
  }
  if (filter.owner_channel_id !== undefined) {
    params.set('owner_channel_id', filter.owner_channel_id);
  }
  const query = params.toString();
  return query.length === 0 ? BASE_PATH : `${BASE_PATH}?${query}`;
}

export async function listFirewallRulesets(
  filter: ListFirewallRulesetsFilter,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<FirewallRulesetSummary[]> {
  const response = await opsApiRequest<ListFirewallRulesetsResponse>(
    listPath(filter),
    { signal },
    fetchImpl,
  );
  return [...response.rulesets];
}

export async function getFirewallRuleset(
  rulesetId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetFirewallRulesetResponse> {
  return opsApiRequest<GetFirewallRulesetResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}`,
    { signal },
    fetchImpl,
  );
}

export async function getFirewallRulesetVersion(
  rulesetId: string,
  version: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<FirewallRulesetVersionRecord> {
  const response = await opsApiRequest<GetFirewallRulesetVersionResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}/versions/${encodeURIComponent(String(version))}`,
    { signal },
    fetchImpl,
  );
  return response.version;
}

export async function createFirewallRuleset(
  body: CreateFirewallRulesetRequest,
  fetchImpl: typeof fetch,
): Promise<FirewallRulesetMutationResponse> {
  return opsApiRequest<FirewallRulesetMutationResponse>(
    BASE_PATH,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function createFirewallRulesetVersion(
  rulesetId: string,
  body: CreateFirewallRulesetVersionRequest,
  fetchImpl: typeof fetch,
): Promise<FirewallRulesetMutationResponse> {
  return opsApiRequest<FirewallRulesetMutationResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}/versions`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * Judge a corpus against one version and, when something is armed, against the
 * armed version too (#1759).
 *
 * It writes an evidence row and nothing else: no ruleset changes, no pointer
 * moves, and the samples themselves are never stored. Activation reads that row
 * — a version with no replay covering the chain about to go live is refused
 * with `FIREWALL_RULESET_REPLAY_REQUIRED`.
 */
export async function replayFirewallRuleset(
  rulesetId: string,
  body: ReplayFirewallRulesetRequest,
  fetchImpl: typeof fetch,
): Promise<ReplayFirewallRulesetResponse> {
  return opsApiRequest<ReplayFirewallRulesetResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}/replay`,
    { method: 'POST', body },
    fetchImpl,
  );
}

/**
 * The only call on this module that changes production. `version: null`
 * disarms without deleting anything.
 */
export async function activateFirewallRuleset(
  rulesetId: string,
  body: ActivateFirewallRulesetRequest,
  fetchImpl: typeof fetch,
): Promise<FirewallRulesetMutationResponse> {
  return opsApiRequest<FirewallRulesetMutationResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}/activate`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function deleteFirewallRuleset(
  rulesetId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteFirewallRulesetResponse> {
  return opsApiRequest<DeleteFirewallRulesetResponse>(
    `${BASE_PATH}/${encodeURIComponent(rulesetId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}
